import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Filter,
  RefreshCw,
  FileText,
  Calendar,
  Paperclip,
  User,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { helpRequestService } from '../services/helpRequestService';

function HelpRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, resolved, closed
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    perPage: 20,
    lastPage: 1,
    currentPage: 1,
  });

  useEffect(() => {
    loadHelpRequests(currentPage);
  }, [filter, currentPage]);

  const loadHelpRequests = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page,
        per_page: 20,
      };
      
      if (filter !== 'all') {
        params.status = filter;
      }
      
      const response = await helpRequestService.getMyHelpRequests(params);
      
      // Handle Laravel pagination format
      if (response.data) {
        setRequests(response.data);
        setPagination({
          total: response.total,
          perPage: response.per_page,
          lastPage: response.last_page,
          currentPage: response.current_page,
        });
      } else {
        // Fallback for non-paginated response
        setRequests(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      console.error('Failed to load help requests:', err);
      setError('Failed to load help requests. Please try again.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'resolved':
      case 'closed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    const badges = {
      open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };
    return badges[statusLower] || badges.open;
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };
    return badges[severity?.toLowerCase()] || badges.medium;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              My Help Requests
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Track your support tickets
            </p>
          </div>
          <button
            onClick={() => {
              setCurrentPage(1);
              loadHelpRequests(1);
            }}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-6 h-6 text-gray-700 dark:text-gray-300 ${
                loading ? 'animate-spin' : ''
              }`}
            />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
          {[
            { value: 'all', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'closed', label: 'Closed' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setFilter(tab.value);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                filter === tab.value
                  ? 'bg-rose-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {loading && requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="w-12 h-12 text-rose-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading requests...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-700 dark:text-red-300 font-semibold mb-2">
              {error}
            </p>
            <button
              onClick={loadHelpRequests}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 text-center shadow-lg">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Help Requests Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You haven't submitted any help requests yet
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Go to Home
            </button>
          </div>
        ) : (
          <>
            {requests.map((request) => (
              <div
                key={request.id || request.req_id}
                className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all border border-gray-200 dark:border-gray-700"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    {getStatusIcon(request.request_status)}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1">
                        {request.req_id}
                      </h3>
                      {request.application_number && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          App #{request.application_number}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getSeverityBadge(
                        request.severity
                      )}`}
                    >
                      {request.severity?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mb-3 flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(
                      request.request_status
                    )}`}
                  >
                    {request.request_status?.replace('_', ' ').toUpperCase()}
                  </span>
                  {request.stage && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {request.stage}
                    </span>
                  )}
                  {request.is_recurring && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                      🔁 Recurring
                    </span>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      Module:
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {request.module}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      Type:
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {request.help_type === 'issue'
                        ? request.issue_or_service_type
                        : request.issue_or_service_type}
                    </span>
                  </div>
                  {request.attachments_count > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Paperclip className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {request.attachments_count} attachment{request.attachments_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  {request.jira_id && (
                    <div className="flex items-center gap-2 text-sm">
                      <ExternalLink className="w-4 h-4 text-gray-500" />
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        Jira:
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {request.jira_id}
                        {request.jira_status && ` (${request.jira_status})`}
                      </span>
                    </div>
                  )}
                  {request.tat && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        TAT:
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {request.tat}
                      </span>
                    </div>
                  )}
                  {request.resolved_by && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        Resolved by:
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {request.resolved_by.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 line-clamp-3">
                  {request.description}
                </p>

                {/* Resolution Remarks */}
                {request.resolution_remarks && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-3">
                    <p className="text-xs font-semibold text-green-800 dark:text-green-300 mb-1">
                      Resolution Remarks:
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-400">
                      {request.resolution_remarks}
                    </p>
                  </div>
                )}

                {/* Footer - Dates */}
                <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Created: {formatDate(request.created_at)}</span>
                  </div>
                  {request.resolved_at && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Resolved: {formatDate(request.resolved_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Pagination */}
            {pagination.lastPage > 1 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Page {pagination.currentPage} of {pagination.lastPage}
                    <span className="ml-2 text-xs">
                      ({pagination.total} total)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === pagination.lastPage}
                      className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default HelpRequests;
